// ExceptionDetail (M1 §8): consequência via chave ICU, link de referência, badge de SLA.
import { Badge, Button, Input } from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HistoryTimeline } from '../../../components';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { apiScopePath } from '../../../lib/workflow/itemRoutes';
import type { WorkflowExceptionDetail } from '../api/contract.preview';
import { useExceptionAction } from '../api/queries';

export interface ExceptionDetailProps {
  readonly exception: WorkflowExceptionDetail;
}

export function ExceptionDetail({ exception }: ExceptionDetailProps) {
  const { t } = useTranslation(['workflow', 'common']);
  const dates = useDateFormatter();
  const scopePath = apiScopePath(exception.scope);
  const action = useExceptionAction();
  const [pending, setPending] = useState<'assign' | 'resolve' | 'dismiss' | null>(null);
  const [reason, setReason] = useState('');

  function submit(): void {
    if (!pending || !reason.trim()) {
      return;
    }
    action.mutate({ scopePath, exceptionId: exception.id, action: pending, reason: reason.trim() });
    setPending(null);
    setReason('');
  }

  const consequenceText = exception.consequence?.messageKey
    ? t(exception.consequence.messageKey, {
        ...exception.consequence.args,
        defaultValue: exception.consequence.detail ?? '',
      })
    : (exception.consequence?.detail ?? exception.detail ?? '');

  return (
    <div className="flex flex-col gap-4" data-testid="exception-detail">
      <div>
        <h1 className="text-title font-semibold">
          {t(`exceptionTypes.${exception.typeCode}.name`, { defaultValue: exception.typeCode })}
        </h1>
        <div className="mt-1 flex flex-wrap gap-2">
          {exception.severity && (
            <Badge variant={exception.overdue ? 'destructive' : 'secondary'}>
              {t(`severities.${exception.severity}`)}
            </Badge>
          )}
          <Badge variant="outline">
            {t('exception.detail.occurrenceCount', { count: exception.occurrenceCount })}
          </Badge>
        </div>
        {consequenceText && <p className="mt-2 text-body">{consequenceText}</p>}
        {exception.source && (
          <p className="mt-1 text-body text-muted-foreground">
            {t('inbox.item.source', { label: exception.source.label ?? exception.source.entityId })}
          </p>
        )}
        <p className="mt-1 text-body text-muted-foreground">
          {t('exception.detail.sla', { when: dates.dateTime(exception.slaDueAt) })}
        </p>
      </div>

      {exception.allowedActions.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="exception-actions">
          {exception.allowedActions.map((allowed) => (
            <Button
              key={allowed}
              size="sm"
              variant={allowed === 'dismiss' ? 'outline' : 'default'}
              onClick={() => {
                setPending(allowed as 'assign' | 'resolve' | 'dismiss');
              }}
              data-testid={`exception-action-${allowed}`}
            >
              {t(`actions.${allowed}`, { defaultValue: allowed })}
            </Button>
          ))}
        </div>
      )}

      {pending && (
        <div
          className="flex flex-col gap-2 rounded-card border p-3"
          data-testid="exception-reason-prompt"
        >
          <label htmlFor="exception-reason" className="text-body font-medium">
            {t(`exception.detail.${pending}ReasonLabel`)}
          </label>
          <Input
            id="exception-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={!reason.trim()}>
              {t('common:actions.confirm')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPending(null);
                setReason('');
              }}
            >
              {t('common:actions.cancel')}
            </Button>
          </div>
        </div>
      )}

      <HistoryTimeline
        entries={exception.history.map((h) => ({
          id: h.id,
          action: h.action,
          actorName: h.actorName,
          actorRole: h.actorRole,
          occurredAt: dates.dateTime(h.occurredAt),
          reason: h.reason ?? null,
          summary: h.summary ?? null,
        }))}
      />
    </div>
  );
}
